import React from 'react';

import CardDialog from '~/components/CardDialog';
import { Heading } from '~/components/core';
import Button from '~/components/core/Atomic/Buttons/Button';
import Grid from '~/components/core/Atomic/Grid/Grid';
import { ShowOnlyTextField } from '~/components/TextFieldFormik';
import type { WorkflowExecutionModel } from '~/server_shared/openapi/data-contracts';

import ExecutionStatusBadge from './ExecutionStatusBadge';

import { useStyles } from '../../../../../assets/styles';

interface ExecutionDetailsDialogProps {
  execution: WorkflowExecutionModel | null;
  open: boolean;
  onClose: () => void;
}

const JsonDisplay: React.FC<{ data: object | null | undefined; label: string }> = ({ data, label }) => {
  const { classes } = useStyles();

  if (!data) return null;

  return (
    <div className="mb-4">
      <ShowOnlyTextField classes={classes} label={label} showOnlyValueComponent={JSON.stringify(data, null, 2)} />
    </div>
  );
};

export const ExecutionDetailsDialog: React.FC<ExecutionDetailsDialogProps> = ({ execution, open, onClose }) => {
  const { classes } = useStyles();

  if (!execution) return null;

  const formatDateTime = (dateTime: string | null) => {
    if (!dateTime) return 'N/A';
    return new Date(dateTime).toLocaleString();
  };

  const footerActions = (
    <Button variant="contained" color="primary" onClick={onClose}>
      Close
    </Button>
  );

  // Check if any JSON data exists to show the section
  const hasJsonData = execution.event_payload || execution.execution_details || execution.raw_status_response;

  return (
    <CardDialog
      open={open}
      onClose={onClose}
      title="Workflow Execution Details"
      maxWidth="md"
      fullWidth
      isDialog
      footerActions={footerActions}
    >
      <div className="space-y-12">
        {/* General Information */}
        <div className="p-6 border-gray-200 rounded-lg border">
          <Heading variant="h4" className="mb-6 pb-3 border-gray-200 border-b">
            General Information
          </Heading>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <ShowOnlyTextField classes={classes} label="Execution ID" showOnlyValueComponent={execution.id} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <ShowOnlyTextField
                classes={classes}
                label="Workflow Name"
                showOnlyValueComponent={execution.workflow_name || 'N/A'}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <ShowOnlyTextField classes={classes} label="Workflow ID" showOnlyValueComponent={execution.workflow_id} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <div className={classes.showField}>
                <span className="MuiTypography-root MuiTypography-caption MuiTypography-displayBlock">Status</span>
                <div style={{ display: 'inline-flex', alignItems: 'center' }}>
                  <ExecutionStatusBadge status={execution.status} />
                </div>
              </div>
            </Grid>
            <Grid item xs={12} sm={6}>
              <ShowOnlyTextField classes={classes} label="Event Key" showOnlyValueComponent={execution.event_key} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <ShowOnlyTextField
                classes={classes}
                label="Workflow Engine"
                showOnlyValueComponent={execution.workflow_engine}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <ShowOnlyTextField
                classes={classes}
                label="Rule Display Name"
                showOnlyValueComponent={execution.rule_display_name || 'N/A'}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <ShowOnlyTextField
                classes={classes}
                label="External Execution ID"
                showOnlyValueComponent={execution.external_execution_id || 'N/A'}
              />
            </Grid>
          </Grid>
        </div>

        {/* Timing Information */}
        <div className="p-6 border-gray-200 rounded-lg border">
          <Heading variant="h4" className="mb-6 pb-3 border-gray-200 border-b">
            Timing Information
          </Heading>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <ShowOnlyTextField
                classes={classes}
                label="Created"
                showOnlyValueComponent={formatDateTime(execution.created_datetime)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <ShowOnlyTextField
                classes={classes}
                label="Submitted"
                showOnlyValueComponent={formatDateTime(execution.submitted_datetime || null)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <ShowOnlyTextField
                classes={classes}
                label="Started"
                showOnlyValueComponent={formatDateTime(execution.started_datetime || null)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <ShowOnlyTextField
                classes={classes}
                label="Completed"
                showOnlyValueComponent={formatDateTime(execution.completed_datetime || null)}
              />
            </Grid>
          </Grid>
        </div>

        {/* Error Information (only if there's an error) */}
        {execution.error_summary && (
          <div className="p-6 border-red-300 rounded-lg border">
            <Heading variant="h4" className="mb-6 pb-3 border-red-200 text-red-800 border-b">
              Error Information
            </Heading>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <ShowOnlyTextField
                  classes={classes}
                  label="Error Summary"
                  showOnlyValueComponent={execution.error_summary}
                />
              </Grid>
            </Grid>
          </div>
        )}

        {/* JSON Data (only if any data exists) */}
        {hasJsonData && (
          <div className="p-6 border-gray-200 rounded-lg border">
            <Heading variant="h4" className="mb-6 pb-3 border-gray-200 border-b">
              JSON Data
            </Heading>
            <div className="space-y-6">
              <JsonDisplay data={execution.event_payload} label="Event Payload" />
              <JsonDisplay data={execution.execution_details} label="Execution Details" />
              <JsonDisplay data={execution.raw_status_response} label="Raw Status Response" />
            </div>
          </div>
        )}
      </div>
    </CardDialog>
  );
};
